// TimeInputCustom.jsx
import React, { useState, useRef, useEffect } from "react";

/**
 * Props:
 *  - value: { hours: <number|string>, minutes: <number|string> }
 *  - rowIndex, dayIndex
 *  - handleValueChange(rowIndex, dayIndex, { field, value })
 *  - handleShiftTimeUpdate(rowIndex, dayIndex)
 *  - mode: "sequential" | "both"  (optional, default "sequential")
 */
const HOURS = Array.from({ length: 15 }, (_, i) => i); // 0..14
const MINUTES = [0, 15, 30, 45];

const small = {
  fontSize: 12,
  height: 30,
  width: 70,
};

const TimeInputCustom = ({
  value = { hours: "", minutes: "" },
  rowIndex,
  dayIndex,
  handleValueChange,
  handleShiftTimeUpdate,
  mode = "sequential",
  hasTime,
}) => {
  // null | 'hour' | 'minute' | 'both'
  const [openStep, setOpenStep] = useState(null);
  const containerRef = useRef(null);

  useEffect(() => {
    if (!openStep) return;
    const onDocDown = (e) => {
      // click outside -> close
      if (!containerRef.current) return;
      if (!containerRef.current.contains(e.target)) {
        setOpenStep(null);
      }
    };
    const onKey = (e) => {
      if (e.key === "Escape") setOpenStep(null);
    };
    document.addEventListener("mousedown", onDocDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [openStep]);

  const openPicker = () => {
    setOpenStep(mode === "both" ? "both" : "hour");
  };

  const openHourPicker = (e) => {
    e.stopPropagation();
    setOpenStep("hour");
  };

  const openMinutePicker = (e) => {
    e.stopPropagation();
    setOpenStep("minute");
  };

  const onHourSelect = (hour) => {
    // store hour
    handleValueChange(rowIndex, dayIndex, { field: "hours", value: hour });
    // close and notify parent (update/save) - independent of minutes
    setTimeout(() => {
      setOpenStep(null);
      // give parent a tick to receive the value
      try {
        handleShiftTimeUpdate(rowIndex, dayIndex);
      } catch (err) {
        // parent may be optional; swallow errors
      }
    }, 80);
  };

  const onMinuteSelect = (minute) => {
    handleValueChange(rowIndex, dayIndex, { field: "minutes", value: minute });
    // close and notify parent (update/save) - independent of hours
    setTimeout(() => {
      setOpenStep(null);
      // give parent a tick to receive the value
      try {
        handleShiftTimeUpdate(rowIndex, dayIndex);
      } catch (err) {
        // parent may be optional; swallow errors
      }
    }, 80);
  };

  // compact pill UI when not open
  if (!openStep) {
    return (
      <div
        ref={containerRef}
        onClick={openPicker}
        style={{
          display: "flex",
          alignItems: "center",
          border: "1px solid #ccc",
          borderRadius: 8,
          overflow: "hidden",
          width: small.width,
          padding: "5px 5px",
          justifyContent: "space-between",
          cursor: "pointer",
          height: small.height,
          userSelect: "none",
          backgroundColor: hasTime ? "#e8e8e8" : "white",
        }}
        title="Click to select time"
      >
        <span
          onClick={openHourPicker}
          style={{
            width: "45%",
            textAlign: "right",
            cursor: "pointer",
            padding: "2px 4px",
            borderRadius: "4px",
            transition: "background-color 0.2s",
          }}
          onMouseEnter={(e) => (e.target.style.backgroundColor = "#f0f0f0")}
          onMouseLeave={(e) => (e.target.style.backgroundColor = "transparent")}
          title="Click to edit hours"
        >
          {value.hours !== "" && value.hours !== null
            ? String(value.hours).padStart(2, "0")
            : "HH"}
        </span>
        <span style={{ fontSize: 12, margin: "0 2px" }}>:</span>
        <span
          onClick={openMinutePicker}
          style={{
            width: "45%",
            textAlign: "left",
            cursor: "pointer",
            padding: "2px 4px",
            borderRadius: "4px",
            transition: "background-color 0.2s",
          }}
          onMouseEnter={(e) => (e.target.style.backgroundColor = "#f0f0f0")}
          onMouseLeave={(e) => (e.target.style.backgroundColor = "transparent")}
          title="Click to edit minutes"
        >
          {value.minutes !== "" && value.minutes !== 0
            ? String(value.minutes).padStart(2, "0")
            : "MM"}
        </span>
      </div>
    );
  }

  // OPEN MODE: sequential => show hour OR minute; both => initially both then hour hides after hour select
  const renderHourList = () => (
    <div
      role="listbox"
      aria-label="Hours"
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 6,
        padding: 8,
        maxHeight: 180,
        overflowY: "auto",
        minWidth: 65,
      }}
    >
      <div
        style={{
          height: 26,
          borderRadius: 6,
          border: "1px solid #ddd",
          background: "#f5f5f5",
          fontSize: 12,
          padding: "0 6px",
          textAlign: "center",
          color: "#666",
          fontWeight: "bold",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        HH
      </div>
      {HOURS.map((h) => {
        const selected = String(h) === String(value.hours);
        return (
          <button
            key={h}
            onClick={() => onHourSelect(h)}
            onKeyDown={(e) => e.key === "Enter" && onHourSelect(h)}
            style={{
              height: 26,
              borderRadius: 6,
              border: selected ? "1px solid #007bff" : "1px solid transparent",
              background: selected ? "#e6f0ff" : "#fff",
              fontSize: 12,
              padding: "0 6px",
              textAlign: "center",
              cursor: "pointer",
            }}
            aria-pressed={selected}
            type="button"
          >
            {String(h).padStart(2, "0")}
          </button>
        );
      })}
    </div>
  );

  const renderMinuteList = () => (
    <div
      role="listbox"
      aria-label="Minutes"
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 6,
        padding: 8,
        maxHeight: 120,
        overflowY: "auto",
        minWidth: 70,
      }}
    >
      <div
        style={{
          height: 26,
          borderRadius: 6,
          border: "1px solid #ddd",
          background: "#f5f5f5",
          fontSize: 12,
          padding: "0 6px",
          textAlign: "center",
          color: "#666",
          fontWeight: "bold",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        MM
      </div>
      {MINUTES.map((m) => {
        const selected = String(m) === String(value.minutes);
        return (
          <button
            key={m}
            onClick={() => onMinuteSelect(m)}
            onKeyDown={(e) => e.key === "Enter" && onMinuteSelect(m)}
            style={{
              height: 26,
              borderRadius: 6,
              border: selected ? "1px solid #007bff" : "1px solid transparent",
              background: selected ? "#e6f0ff" : "#fff",
              fontSize: 12,
              padding: "0 6px",
              textAlign: "center",
              cursor: "pointer",
            }}
            aria-pressed={selected}
            type="button"
          >
            {String(m).padStart(2, "0")}
          </button>
        );
      })}
    </div>
  );

  return (
    <div
      ref={containerRef}
      style={{
        display: "inline-block",
        position: "relative",
        width: small.width,
      }}
    >
      {/* the compact pill while open (keeps layout stable) */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          border: "1px solid #ccc",
          borderRadius: 8,
          overflow: "hidden",
          width: "100%",
          padding: "0px 5px",
          justifyContent: "space-between",
          height: small.height,
          background: "#fff",
        }}
      >
        <span
          onClick={openHourPicker}
          style={{
            width: "45%",
            textAlign: "right",
            cursor: "pointer",
            padding: "2px 4px",
            borderRadius: "4px",
            transition: "background-color 0.2s",
            backgroundColor:
              openStep === "hour" || openStep === "both"
                ? "#e6f0ff"
                : "transparent",
          }}
          onMouseEnter={(e) => {
            if (openStep !== "hour" && openStep !== "both") {
              e.target.style.backgroundColor = "#f0f0f0";
            }
          }}
          onMouseLeave={(e) => {
            if (openStep !== "hour" && openStep !== "both") {
              e.target.style.backgroundColor = "transparent";
            }
          }}
          title="Click to edit hours"
        >
          {value.hours !== "" && value.hours !== null
            ? String(value.hours).padStart(2, "0")
            : "HH"}
        </span>
        <span style={{ fontSize: 12, margin: "0 2px" }}>:</span>
        <span
          onClick={openMinutePicker}
          style={{
            width: "45%",
            textAlign: "left",
            cursor: "pointer",
            padding: "2px 4px",
            borderRadius: "4px",
            transition: "background-color 0.2s",
            backgroundColor:
              openStep === "minute" || openStep === "both"
                ? "#e6f0ff"
                : "transparent",
          }}
          onMouseEnter={(e) => {
            if (openStep !== "minute" && openStep !== "both") {
              e.target.style.backgroundColor = "#f0f0f0";
            }
          }}
          onMouseLeave={(e) => {
            if (openStep !== "minute" && openStep !== "both") {
              e.target.style.backgroundColor = "transparent";
            }
          }}
          title="Click to edit minutes"
        >
          {value.minutes !== "" && value.minutes !== null
            ? String(value.minutes).padStart(2, "0")
            : "MM"}
        </span>
      </div>

      {/* overlay panel */}
      <div
        style={{
          position: "absolute",
          left: 0,
          top: "calc(100% + 6px)",
          zIndex: 9999,
          background: "#fff",
          border: "1px solid #ddd",
          borderRadius: 10,
          boxShadow: "0 6px 18px rgba(0,0,0,0.08)",
          display: "flex",
          gap: 6,
          padding: 6,
          // width can grow to show both side-by-side if needed
          minWidth: 65,
        }}
      >
        {/* hour column */}
        {openStep === "hour" && renderHourList()}

        {/* both mode initial */}
        {openStep === "both" && (
          <>
            {renderHourList()}
            <div style={{ width: 1, background: "#f0f0f0", margin: "4px 0" }} />
            {renderMinuteList()}
          </>
        )}

        {/* minute column (sequential or after hour selected in both mode) */}
        {openStep === "minute" && renderMinuteList()}
      </div>
    </div>
  );
};

export default TimeInputCustom;
